import {
    setTrackIsLoading, 
    setEffectParameterValues
} from 'actions/index.js';
import Tone from 'tone';
import {mod} from "utils/mod.js";
import {getImaginaryReal, getImaginaryRealSync} from 'constants/waveforms.js';
import {sleep} from 'utils/sleep.js';
import {bulkUpdate} from 'utils/bulkUpdate.js';
import {loadParameterFromJson, saveParameterToJson} from 'parameters/parameterJsonFunctions.js';
let counter = 0;

export default superclass => class extends superclass  {
    
    getJson(isPreset) {
        let json = {
            id: this.id,
            mix: this.mixParameter.get(true),
            algorithm: this.algorithmParameter.get(true),
            ampEnvelope: this.ampEnvelope.getJson(isPreset),
            polyphony: this.polyphonyParameter.get(),
            instrumentType: this.getInstrumentType(),
            filterCutoff: this.filterCutoff,
            filterQ: this.filterQ,
            hpFilterCutoff: this.hpFilterCutoff,
            fmParameters: this.fmParameters,
            hpFilterQ: this.hpFilterQ,
            ampEnvelope: this.ampEnvelope.getJson(isPreset),
            filterEnvelope: this.filterEnvelope.getJson(isPreset),
            hpFilterEnvelope: this.hpFilterEnvelope.getJson(isPreset),
            pitchEnvelope: this.pitchEnvelope.getJson(isPreset),
            oscillators: this.oscillatorNodes.map(osc => osc.getJson(isPreset)),
            noiseLevel: this.noiseLevelParameter.get(true),
            pitchBend: this.pitchBendParameter.get(true),
        };

        json = saveParameterToJson(json, 'noiseLevelParameter', this.noiseLevelParameter, undefined, isPreset);
        json = saveParameterToJson(json, 'filterCutoffParameter', this.filterCutoffParameter, undefined, isPreset);
        json = saveParameterToJson(json, 'hpFilterCutoffParameter', this.hpFilterCutoffParameter, undefined, isPreset);
        json = saveParameterToJson(json, 'hpFilterQParameter', this.hpFilterQParameter, undefined, isPreset);
        json = saveParameterToJson(json, 'filterQParameter', this.filterQParameter, undefined, isPreset);

        json = saveParameterToJson(json, 'algorithmParameter', this.algorithmParameter, undefined, isPreset);
        json = saveParameterToJson(json, 'mixParameter', this.mixParameter, undefined, isPreset);
        json = saveParameterToJson(json, 'pitchBendParameter', this.pitchBendParameter, undefined, isPreset);
        return json;
    }


    async fromJson(json, ignoreId=false, isPreset=false) {
        let c=0;
        if (!json) {
            return {};
        }
        if (json.fmParameters && !ignoreId) {
            this.fmParameters = json.fmParameters;
        }
        if (!ignoreId && json.id) {
            this.id = json.id;
        }
        while (!getImaginaryRealSync("01_Saw") || !getImaginaryRealSync("03_Square")) {
            await sleep(100);
            if (++c > 50) {
                return;
            }
        }
        this.ampEnvelope.release = 50;
        this.clearVoiceData();
        const a = new Date().getTime();
        this.loaded = false;
        this.track.trackIsLoading = true;
        await sleep(220);
        this.getParameters().forEach(
            x => x.isLoaded = false);


        let bulkUpdates = {};
        if (json.polyphony !== undefined) {
            let {id, value} = this.polyphonyParameter.set(json.polyphony, undefined, true);
            bulkUpdates[id] = value;
        }

        // await sleep(50);

        bulkUpdates = {
            ... bulkUpdates,
            ... await this.fromOscillatorsJson(json, ignoreId),
        };
        
        if (json.algorithm !== undefined) {
            bulkUpdate(this.algorithmParameter, json.algorithm, bulkUpdates);
        }

        if (isPreset) {
            bulkUpdates = {
                ... bulkUpdates,
                ...this.scaleToC()
            };
        }

        bulkUpdates = {
            ... bulkUpdates,
            ... this.fromJsonRest(json, ignoreId)
        };
        const b = new Date().getTime();

        // await sleep(200);
        this.keepParametersInSync();
        return bulkUpdates;
    }

    fromJsonSync(json, ignoreId=false) {
        if (!ignoreId && json.id) {
            this.id = json.id;
        }
        this.loaded = false;
        this.track.trackIsLoading = true;
        this.getParameters().forEach(
            x => x.isLoaded = false);


        let bulkUpdates = {};
        if (json.polyphony !== undefined) {
            let {id, value} = this.polyphonyParameter.set(json.polyphony, undefined, true);
            bulkUpdates[id] = value;
        }

        if (json.algorithm !== undefined) {
            bulkUpdate(this.algorithmParameter, json.algorithm, bulkUpdates);
        }

        bulkUpdates = {
            ... bulkUpdates,
            ... this.fromOscillatorsJsonSync(json, ignoreId),
            ... this.fromJsonRest(json, ignoreId)
        };
    }

    fromJsonRest(json, ignoreId=false) {
        let id = ++counter;
        let bulkUpdates = {};
        bulkUpdates = {
            ... bulkUpdates,
            ... this.ampEnvelope.fromJson(json.ampEnvelope)
        };


        if (json.noiseLevel !== undefined) {
            bulkUpdate(this.noiseLevelParameter, json.noiseLevel, bulkUpdates);
            bulkUpdates = {
                ... bulkUpdates,
            ... loadParameterFromJson(
                json, 'noiseLevelParameter', this.mixParameter, this.sequencerEngine, ignoreId),
            };
        } else {
            this.noiseLevelParameter.set(0);
        }
        bulkUpdates = {
            ... bulkUpdates,
            ... loadParameterFromJson(
                json, 'mixParameter', this.mixParameter, this.sequencerEngine, ignoreId),
            ... loadParameterFromJson(
                json, 'algorithmParameter', this.algorithmParameter, this.sequencerEngine, ignoreId),
            ... loadParameterFromJson(
                json, 'pitchBendParameter', this.pitchBendParameter, this.sequencerEngine, ignoreId)
        };

        if (json.pitchBend) {
            bulkUpdate(this.pitchBendParameter, json.pitchBend, bulkUpdates);
        } else {
            bulkUpdate(this.pitchBendParameter, 0, bulkUpdates);
        }


        if (json.mix !== undefined) {
            bulkUpdate(this.mixParameter, json.mix, bulkUpdates);
        }

        if (this.detuneOffset) {
            let {id, value} = this.pitchBendParameter.set(
                this.pitchBendParameter.get(true) + this.detuneOffset);
            bulkUpdates[id] = value;
        }

        return bulkUpdates;
    }

    keepParametersInSync() {
        // go thru all the poly parameters and make sure the underlying audioParams
        // are 0
        this.getParameters().forEach(
            p => {
                if (p.depthSignal && p.depthParameter) {
                    if (p.depthSignal.value !== p.depthParameter.get(true)) {
                        p.depthParameter.refresh(true, true);
                    }
                }
                if (p._audioParams) {
                    p._audioParams.filter(
                        x => x.input && x.value !== 0)
                        .forEach(x => {
                            x.value = 0;
                        });
                }
                if (p.audioParam && p.audioParam.value !== p.get(true)) {
                    p.refresh(true, true);
                }
            });
        
        this.loaded = true;
        this.getParameters().forEach(
            x => x.isLoaded = true);
        this.track.trackIsLoading = false;
    }

    fromOscillatorsJsonSync(json, ignoreId=false) {
        let bulk = {};
        let promises = [];
        this.instrumentType =  json.instrumentType;
        for (let i in this.oscillatorNodes) {
            i = parseInt(i);
            let oscillator = this.oscillatorNodes[i];
            bulk = {
                ... bulk,
                ... oscillator.fromJson(json.oscillators[i], ignoreId)
            };
        }

        return {
            ... bulk,
            ... this.loadFilterJson(json, ignoreId)
        };
    }

    async loadOscillators(json, ignoreId=false) {
        let promises = [];
        let a = new Date().getTime();
        let bulk = {
            ... bulk,
            ... await this.oscillatorNodes[0].fromJson(json.oscillators[0], ignoreId)
        };
        for (let i in this.oscillatorNodes) {
            i = parseInt(i);
            if (i === 0) {
                continue;
            }
            let oscillator = this.oscillatorNodes[i];
            bulk = {
                ... bulk,
                ... oscillator.fromJson(json.oscillators[i], ignoreId)
            };
            // promises.push(oscillator.fromJson(json.oscillators[i], ignoreId));
        }
        /*
        let results = await Promise.all(promises);
        for (let result of results) {
            bulk = {
                ... bulk,
                ... result
            };
        }
        */
        let b = new Date().getTime();
        return bulk;
    }

    async fromOscillatorsJson(json, ignoreId=false) {
        this.instrumentType =  json.instrumentType;
        let bulk = await this.loadOscillators(json, ignoreId);
        return {
            ... bulk,
            ... this.loadFilterJson(json, ignoreId)
        };

   }

    loadFilterJson(json, ignoreId=false) {
        let bulk = {};
        bulk = {
            ... bulk,
            ... this.filterEnvelope.fromJson(json.filterEnvelope, ignoreId),
            ... this.ampEnvelope.fromJson(json.ampEnvelope, ignoreId),
            ... this.pitchEnvelope.fromJson(json.pitchEnvelope, ignoreId),
            ... loadParameterFromJson(
                json, 'filterCutoffParameter', this.filterCutoffParameter, this.sequencerEngine, ignoreId, true),
            ... loadParameterFromJson(
                json, 'filterQParameter', this.filterQParameter, this.sequencerEngine, ignoreId, true),
            ... loadParameterFromJson(
                json, 'hpFilterCutoffParameter', this.hpFilterCutoffParameter, this.sequencerEngine, ignoreId, true),
            ... loadParameterFromJson(
                json, 'hpFilterQParameter', this.hpFilterQParameter, this.sequencerEngine, ignoreId, true)
        };
        if (json.hpFilterEnvelope) {
            bulk = {
                ... bulk,
                ... this.hpFilterEnvelope.fromJson(json.hpFilterEnvelope)
            };
        }
        
        bulkUpdate(this.filterCutoffParameter, json.filterCutoff, bulk);
        bulkUpdate(this.filterQParameter, json.filterQ, bulk);
        if (json.hpFilterCutoff !== undefined) {
            bulkUpdate(this.hpFilterCutoffParameter, json.hpFilterCutoff, bulk);
            bulkUpdate(this.hpFilterQParameter, json.hpFilterQ, bulk);
        }

        return bulk;
    }

    scaleToC() {
        let osc = this.getLoudestOsc();
        let harm = osc.harmonicityParameter.get(true);
        let oscDetune = osc.detuneParameter.get(true);
        /*
        if (Math.round(harm) === 0) {
            harm++;
        }
        */

        let oldFreq = this.carrierOscillator.oscillatorFrequencyParameter.get(true);

        // We have a synth w harmonicity=3 with Root Note=B.
        // We want it to be in C so we have to calculate how far off we are and then adjust
        let baseFreq = new Tone.Frequency('C3').toFrequency();

        //let baseFreq = new Tone.Frequency(new Tone.Frequency('C3').toMidi() + oldFreq, "midi").toFrequency();

        let diffs = [];
        let minDiff = Infinity;
        /*
        for (let i=1; i < 12; i++) {
            // loop thru octaves to find which one matches the best
            let c = new Tone.Frequency('C' + i).toFrequency();

            let goal = c;
            */
        let goal = baseFreq;
        let _cent = cent(baseFreq, oscDetune);
        let diff = ((baseFreq+ _cent) *harm) - goal;
        minDiff = diff;
        //console.log("goal=%s harm*baseFreq=%s diff=%s", goal, baseFreq+(baseFreq+_cent)*harm, diff);
        /*
            if (Math.abs(diff) < Math.abs(minDiff)) {
                minDiff = diff;
            }
        }
        */

        //console.log("MIN DIFF=", minDiff);

        //console.log("baseFreq=", baseFreq);
        let newBaseFrequencyRaw = baseFreq + minDiff;
        // calcualte the note
        //console.log("newBaseFrequencyRaw=%s", newBaseFrequencyRaw);
        let newBaseFrequencyMidiNote = new Tone.Frequency(newBaseFrequencyRaw).toMidi();
        //console.log("new base frequency midi note=%s C3=%s", newBaseFrequencyMidiNote, new Tone.Frequency("C3").toMidi());
        let globalFrequency = newBaseFrequencyMidiNote - new Tone.Frequency("C3").toMidi();

        // now tune it

        let roundedFrequency = new Tone.Frequency(newBaseFrequencyMidiNote, "midi").toFrequency();
        //console//.log("rounded frequency=", roundedFrequency);
        let detuneOffset = newBaseFrequencyRaw - roundedFrequency;
        //console.log("detune offset=", detuneOffset);
        if (detuneOffset < 0) {
            let prevFreq = new Tone.Frequency(newBaseFrequencyMidiNote-1, "midi").toFrequency();
            let detuneInCents = 100*detuneOffset/(roundedFrequency - prevFreq);
//            console.log("detune in cents=", detuneInCents);
            this.detuneOffset = 0;//detuneInCents;
        } else {
            let nextFreq = new Tone.Frequency(newBaseFrequencyMidiNote+1, "midi").toFrequency();
            let detuneInCents = 100*detuneOffset/(nextFreq - roundedFrequency)
//            console.log("detune in cents=", detuneInCents);
            this.detuneOffset = 0;//detuneInCents;
        }
//        console.log("globalFreq=%s detuneOffset=%s oldFreq=%s", globalFrequency, detuneOffset, oldFreq);

        // based on the difference adjust the base frequency AND global detune

        let newFreq = -globalFrequency;

        let moddedFreq = mod(newFreq, 12);

        let oldie = Math.floor(oldFreq / 12)*12;
        newFreq = oldie + moddedFreq;

        // finally fgure out what o
        let {id, value} = this.carrier.oscillatorFrequencyParameter.set(newFreq, false, true);
        let bulk = {};
        bulk[id] = value;
        return bulk;
    }

    getLoudestOsc(oscillators=this.oscillators) {
        if (oscillators.length === 0) {
            return undefined;
        }
        let connectedToMix = [];
        for (let o of this.oscillators) {
            let edges = this.adjacencyLists[o.id];
            if (edges.some(x => x.effect.name === "X" || x.effect.name === "Y")) {
                connectedToMix.push(o);
            }
        }
        let osc;
        let level=-Infinity;
        for (let o of oscillators) {
            if (!connectedToMix.includes(o)) {
                continue;
            }
            let _level = o.oscillatorLevelParameter.get(true);
            if (_level > level) {
                osc = o;
                level=_level;
            }
        }
        return osc;
    }
}

const cent = (freq, _cent) => {
    // given a frequency and cent return the frequency w the cent applied
    let midi1 = new Tone.Frequency(freq).toMidi();
    let midi2 = midi1 + 1;

    let freq2 = new Tone.Frequency(midi2, "midi").toFrequency();
    let val = freq + (freq2 - freq) * (_cent/100);
    //console.log("freq1=%s freq2=%s cent=%s val=%s",
    //            freq, freq2, _cent,val);
    return val;
};
